import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import Header from 'components/ui/Header';
import Icon from 'components/AppIcon';
import Footer from 'pages/landing-page/components/Footer';

const statusConfig = {
  sent: {
    label: 'Inviato',
    className: 'bg-gray-100 text-gray-600 border border-gray-200',
  },
  shortlisted: {
    label: 'Selezionato dal cliente',
    className: 'bg-green-100 text-green-700 border border-green-200',
  },
  accepted: {
    label: 'Contatto sbloccato ✓',
    className: 'bg-[#104d98] text-white border border-[#104d98] font-semibold',
  },
};

const ProfessionalTrattative = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('tutti');

  useEffect(() => {
    if (!user) {
      navigate('/login-form');
      return;
    }
    fetchQuotes();
  }, [user]);

  const fetchQuotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase?.from('quotes')?.select('*, service_requests(id, title, service_type, location_name)')?.eq('professional_id', user?.id)?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setQuotes(data || []);
      console.log(data);
    } catch (err) {
      console.error('Error fetching quotes:', err);
      setError('Errore durante il caricamento delle trattative.');
    } finally {
      setLoading(false);
    }
  };

  const acceptedQuotes = quotes?.filter((q) => q?.status === 'accepted');

  const filteredQuotes = activeTab === 'tutti'
    ? quotes
    : activeTab === 'inviati'
    ? quotes?.filter((q) => q?.status === 'sent')
    : quotes?.filter((q) => q?.status === 'accepted');

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 pt-16">
      <Header />
      <div className="flex-1 w-full px-6 md:px-12 lg:px-16 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/professional-dashboard')}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#2780b5] hover:bg-gray-50 rounded-lg px-3 py-2 -ml-3 mb-4 transition-all duration-150 self-start"
          style={{ position: 'relative', zIndex: 10 }}
        >
          <Icon name="ArrowLeft" size={18} />
          <span className="text-sm font-medium">Torna alla dashboard</span>
        </button>

        {/* Page title with left border accent */}
        <h1 className="text-xl font-bold text-[#013776] mb-6 pl-4 border-l-4 border-[#2780b5]">
          Le mie trattative
        </h1>

        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="w-8 h-8 border-4 border-[#2780b5] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4">
            {error}
          </div>
        )}

        {!loading && !error && quotes?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Icon name="FileText" size={48} className="text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">Non hai ancora inviato nessun preventivo.</p>
          </div>
        )}

        {!loading && !error && quotes?.length > 0 && (
          <>
            {/* Accepted info box — full width, above the grid */}
            {acceptedQuotes?.length > 0 && (
              <div className="w-full mb-6 bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-green-800 text-sm font-medium">
                  {acceptedQuotes?.length === 1
                    ? '1 preventivo accettato. Aspetta che il cliente ti contatti.'
                    : `${acceptedQuotes?.length} preventivi accettati. Aspetta che il cliente ti contatti.`}
                </p>
              </div>
            )}

            {/* Filter tabs */}
            <div className="flex gap-2 mb-6">
              {[
                { key: 'tutti', label: 'Tutti' },
                { key: 'inviati', label: 'Inviati' },
                { key: 'sbloccati', label: 'Sbloccati' },
              ]?.map((tab) => (
                <button
                  key={tab?.key}
                  onClick={() => setActiveTab(tab?.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 ${
                    activeTab === tab?.key
                      ? 'bg-[#2780b5] text-white'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* 2-column grid on desktop, single column on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredQuotes?.map((quote) => {
                const status = statusConfig?.[quote?.status] || statusConfig?.sent;
                return (
                  <div
                    key={quote?.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
                  >
                    {/* Top row: title + status badge */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <h2 className="text-base font-bold text-[#013776] leading-snug">
                        {quote?.service_requests?.title || '—'}
                      </h2>
                      <span className={`inline-block text-xs px-3 py-1 rounded-full whitespace-nowrap ${status?.className}`}>
                        {status?.label}
                      </span>
                    </div>

                    {/* Mid section: SERVIZIO | LOCALITÀ */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-5">
                      <div>
                        <span className="text-gray-400 text-xs uppercase tracking-wide font-medium">Servizio</span>
                        <p className="text-gray-800 mt-0.5">{quote?.service_requests?.service_type || '—'}</p>
                      </div>
                      <div>
                        <span className="text-gray-400 text-xs uppercase tracking-wide font-medium">Località</span>
                        <p className="text-gray-800 mt-0.5">{quote?.service_requests?.location_name || '—'}</p>
                      </div>
                    </div>

                    {/* Bottom: price */}
                    <div className="border-t border-gray-100 pt-4">
                      <span className="text-gray-400 text-xs uppercase tracking-wide font-medium">Il mio preventivo</span>
                      <p className="text-2xl font-bold text-[#2780b5] mt-1">
                        {quote?.preview_price != null ? `€ ${Number(quote?.preview_price)?.toLocaleString('it-IT')}` : '—'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ProfessionalTrattative;
